import { GameState, ReadingResult } from "@shared/types";
import { GameEvent } from "../enums/GameEvent";
import { GameRouteService } from "../services/GameRouteService";
import { GameEventService, UpdateReadContentEvent } from "../services/GameEventService";
import { css, html, htmlArray } from "../helpers/webComponents";

/** CSS affecting the {@link ReadComponent} */
const styles: string = css`
    :host {
        position: absolute; /* Position the arrows relative to the header */
        top: -2%;
        left: -1.5%;
        width: 103%; /* Match the width of the container (header's image) */
        height: 103%; /* Match the height to the image */
        pointer-events: none; /* Make clicks go through navigation-arrows element*/
        z-index: 0;
    }

    // make the component inactive when no item is found
    :host(:not(:has(.item-displayal))) {
        display: none;
        z-index: 999;
    }

    .dark-overlay {
        position: relative;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.61);
        display: flex;
        justify-content: center; /* Center the content horizontally */
        align-items: center;
        z-index: 9999;
    }

    .item-displayal {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: auto;
        display: flex;
        pointer-events: auto;
        justify-content: center; /* Ensure the content (image) is centered horizontally */
        align-items: center;

        .zoom-wrapper {
            position: relative;
            overflow: hidden;
            width: 100%;
            height: 100%;
        }

        .zoomable {
            position: absolute;
            top: 50%;
            left: 50%;
            transition: transform 0.2s ease;
            transform-origin: center center;
            height: 90%;
            width: auto;
            object-fit: contain;
            border-radius: 8px;
            transform: translate(-50%, -50%) scale(1);
        }
    }
    .cross-button {
        font-family: "Onesize";
        position: absolute;
        font-size: 24px;
        top: 10px;
        right: 10px;
        height: 13%;
        width: auto;
        border: none;
        border-left: 2px solid #52648b;
        border-right: 2px solid #52648b;
        aspect-ratio: 1;
        cursor: var(--cursor-hover);
        z-index: 10;
    }
    .cross-button:hover {
        color: red; /* Optional hover effect */
    }
    .cross-button:active {
        color: red;
        cursor: var(--cursor-active);
    }

    .arrow {
        height: 20%;
        position: absolute;
        top: 50%;
        z-index: 5;
        cursor: var(--cursor-hover);
    }
    .arrow.disabled {
        opacity: 0.5;
        background-color: #6aa7df76;
        border-top: 2px solid #9ccfff61;
        outline: 1px solid #3f539790;
        border-bottom: 2px solid #4261a589;
        border-radius: 4px;

        pointer-events: none;
        cursor: var(--cursor-default);
    }
    .arrow:active {
        cursor: var(--cursor-active);
    }

    .arrow.left {
        left: 10px;
    }
    .arrow.right {
        right: 10px;
    }

    .btn {
        position: absolute;
        background-color: #6aa6df;
        outline: 1px solid #3f5497;
        border-radius: 4px;
        border-top: 2px solid #9ccfff;
        margin: 0;
        text-transform: uppercase;
        cursor: var(--cursor-hover);
        display: inline-block;
        user-select: none;
        border-bottom: 2px solid #4261a5;
        transition: all 0.1s ease-in-out;
    }

    .btn:hover {
        filter: brightness(1.1);
        transform: translateY(-2px);
        border-bottom-width: 4px;
        margin-bottom: 6px;
    }

    .btn:active,
    .btn:focus {
        cursor: var(--cursor-active);
        filter: brightness(0.9);
        transform: translateY(0px);
        border-bottom-width: 2px;
    }
`;

/**
 * A Item Reading Web Component
 *
 * @remarks Will listen for the Update Read Content event and render the overlook of the item if possible
 */
export class ReadComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();

    private currentPageIndex: number = 0;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Listen for the Update Read Content event
        this._gameEventService.addGameEventListener<UpdateReadContentEvent>(GameEvent.UpdateReadContent, event => {
            this.render(event.readingResult);
        });
    }

    /**
     * Render the contents of <game-readpanel>
     *
     * @param readingResult Reading result containing the images and text to display later + the alias of the object that Read action has been executed on
     */
    private render(readingResult: ReadingResult): void {
        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = "";

            const elements: HTMLElement[] = htmlArray`
                    <style>
                        ${styles}
                    </style>
                    <div class="dark-overlay">
                        ${this.renderItem(readingResult)}
                    </div>                
                `;

            this.shadowRoot.append(...elements);

            this.addZoomEffect();
        }
    }

    /**
     * Render the images and buttons such as close (stop reading), previous page and next page
     *
     * @param readingResult Reading result containing the images and text to display later + the alias of the object that Read action has been executed on
     *
     * @returns HTML element with the images and buttons
     */
    private renderItem(readingResult: ReadingResult): HTMLElement {
        const currentImage: string = `/assets/img/rooms/layers/reading/${readingResult.images[this.currentPageIndex]}`;

        const element: HTMLElement = html`
            <div class="item-displayal">
                <div class="zoom-wrapper">
                    <img class="page zoomable" src=${currentImage}>
                </div>
                <!-- Left Arrow (Previous) -->
                ${this.currentPageIndex > 0
                    ? "<img class='arrow left btn' data-direction='previous' src='/assets/img/rooms/layers/reading/read-left.png'>"
                    : "<img class='arrow left disabled' data-direction='previous' src='/assets/img/rooms/layers/reading/read-left.png'>"}

                <!-- Right Arrow (Next) -->
                ${this.currentPageIndex < readingResult.images.length - 1
                    ? "<img class='arrow right btn' data-direction='next' src='/assets/img/rooms/layers/reading/read-right.png'>"
                    : "<img class='arrow right disabled' data-direction='next'  src='/assets/img/rooms/layers/reading/read-right.png'>"}

                <button class="cross-button btn">X</button>
            </div>
        `;

        // Idea: https://code-boxx.com/image-zoom-css-javascript/

        // Add event listeners for the arrows
        element.querySelectorAll(".arrow").forEach(arrow => {
            arrow.addEventListener("click", event => this.handleArrowClick(event as MouseEvent, readingResult));
        });

        element.querySelector(".cross-button")?.addEventListener("click", async () => this.handleCloseReading(readingResult));

        return element;
    }

    /**
     * Handle the click on the previous or next page button to change the page
     *
     * @param event The mouseEvent where the target arrow is selected
     *
     * @param ReadingResult The information that has been previously received in order to rerender the component with the correct information
     */
    private handleArrowClick(event: MouseEvent, readingResult: ReadingResult): void {
        this.playSound("book-turn.mp3");

        const direction: string = (event.target as HTMLElement).getAttribute("data-direction")!;

        if (direction === "previous" && this.currentPageIndex > 0) {
            this.currentPageIndex--; // Go to the previous image
        }
        else if (direction === "next" && this.currentPageIndex < readingResult.images.length - 1) {
            this.currentPageIndex++; // Go to the next image
        }

        // Re-render with the updated index
        this.render(readingResult);
    }

    /**
     * Handle the click on the cross - close button and dispatch a new Event in order to update the text
     *
     * @param readingResult Reading result containing the text to update and the alias of the object that Read action has been executed on
     */
    private async handleCloseReading(readingResult: ReadingResult): Promise<void> {
        this.playSound("book-close.mp3");
        // Execute the navigation and update the game state
        const state: GameState | undefined = await this._gameRouteService.executeStopReading(readingResult.object, readingResult.text);

        if (state === undefined) {
            return;
        }

        this._gameEventService.stopReading(state);
    }

    private playSound(name: string): void {
        this._gameEventService.playSound(name);
    }

    /**
     * Adds a zoom-in effect to an image on mouse hover and movement.
     *
     * @remarks
     * This method attaches event listeners to a `.zoom-wrapper` container and a `.zoomable` image.
     * When the mouse moves within the container, the image scales up with the zoom centered around the cursor.
     * When the mouse leaves the container, the image resets to its original scale.
     *
     * The image is expected to be centered with `transform: translate(-50%, -50%)` and styled with `position: absolute`.
     */
    private addZoomEffect(): void {
        if (!this.shadowRoot) return;

        // Select the image and its zoom container
        const zoomableImage: HTMLImageElement = this.shadowRoot.querySelector(".zoomable") as HTMLImageElement;
        const zoomWrapper: HTMLDivElement = this.shadowRoot.querySelector(".zoom-wrapper") as HTMLDivElement;

        // Handle mouse movement for zoom positioning
        zoomWrapper.addEventListener("mousemove", (e: MouseEvent) => {
            // This is the size and position relative to the viewport
            const rect: DOMRect = zoomWrapper.getBoundingClientRect();

            // Calculate mouse position relative to container as percentages
            const xPercent: number = ((e.clientX - rect.left) / rect.width) * 100;
            const yPercent: number = ((e.clientY - rect.top) / rect.height) * 100;

            // Adjust zoom origin based on cursor location
            zoomableImage.style.transformOrigin = `${xPercent}% ${yPercent}%`;
            zoomableImage.style.transform = "translate(-50%, -50%) scale(2.5)";
        });

        // Reset image zoom on mouse leave
        zoomWrapper.addEventListener("mouseleave", () => {
            zoomableImage.style.transform = "translate(-50%, -50%) scale(1)";
            zoomableImage.style.transformOrigin = "center center";
        });
    }
}
